import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Lock, Plus, X } from "lucide-react";
import { Btn, ErrorNote, Field, Input, Select } from "@/components/kmt/ui";
import { TimeSelect } from "@/components/kmt/TimeSelect";
import { errMsg, useOwnerApi } from "@/lib/api";
import { SERVICES, ct, usd } from "@/lib/format";

const DISCOUNTS = [
  { value: "none", label: "No discount" },
  { value: "courtesy", label: "Courtesy discount" },
  { value: "voucher", label: "$40 voucher" },
];

const blank = (service: string) => ({
  miles: "", service, stops: [] as string[], wait_minutes: "0", extra_waiting: "", fees: [] as { label: string; amount: string }[],
  discount_type: "none", discount_amount: "", override: false, fare_override: "", pickup_date: "", pickup_time: "",
});

const num = (s: string) => (s === "" ? NaN : Number(s));

export function FareForm({ service = "standard", lockService = false, showPickup = false, onIssue }: {
  service?: string; lockService?: boolean; showPickup?: boolean; onIssue?: (p: any) => Promise<void>;
}) {
  const api = useOwnerApi();
  const [f, setF] = useState(blank(service));
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: any) => setF((s) => ({ ...s, [k]: v }));

  const payload = useMemo(() => ({
    miles: num(f.miles), service: f.service,
    stops: f.stops.map(num).filter((n) => !isNaN(n)),
    wait_minutes: parseInt(f.wait_minutes || "0", 10) || 0,
    extra_waiting: num(f.extra_waiting) || 0,
    fees: f.fees.filter((x) => x.label && x.amount !== "").map((x) => ({ label: x.label, amount: num(x.amount) })),
    discount_type: f.discount_type === "none" ? null : f.discount_type,
    discount_amount: f.discount_type === "courtesy" ? num(f.discount_amount) || 0 : 0,
    fare_override: f.override && f.fare_override !== "" ? num(f.fare_override) : null,
    ...(showPickup && f.pickup_date && f.pickup_time ? { pickup_date: f.pickup_date, pickup_time: f.pickup_time } : {}),
  }), [f, showPickup]);

  useEffect(() => {
    if (!(payload.miles > 0)) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setRes((await api.post("/fare/calculate", payload)).data);
        setErr("");
      } catch (e) {
        setRes(null);
        setErr(errMsg(e));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [payload, api]);

  const issue = async () => {
    setBusy(true);
    try {
      await onIssue!(payload);
      setF(blank(service));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-8 xl:grid-cols-[1.1fr_1fr]" data-testid="fare-form">
      <div className="grid content-start gap-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Trip miles" htmlFor="ff-miles">
            <Input id="ff-miles" type="number" step="0.01" min="0" inputMode="decimal" value={f.miles} onChange={(e) => set("miles", e.target.value)} data-testid="fare-miles-input" />
          </Field>
          <Field label="Service" htmlFor="ff-service">
            <Select id="ff-service" options={SERVICES} value={f.service} disabled={lockService} onChange={(e) => set("service", e.target.value)} data-testid="fare-service-select" />
          </Field>
        </div>
        {showPickup && (
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Pickup date (optional)" htmlFor="ff-date">
              <Input id="ff-date" type="date" value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="fare-pickup-date" />
            </Field>
            <Field label="Pickup time" htmlFor="ff-time">
              <TimeSelect id="ff-time" value={f.pickup_time} onChange={(v) => set("pickup_time", v)} testid="fare-pickup-time" />
            </Field>
          </div>
        )}
        <AmountList label="Added stops ($3–5 each, owner-approved)" items={f.stops} onChange={(v) => set("stops", v)} testid="fare-stop" min={3} max={5} />
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Minutes waited" hint="$10 flat from minute 16" htmlFor="ff-wait">
            <Input id="ff-wait" type="number" min="0" value={f.wait_minutes} onChange={(e) => set("wait_minutes", e.target.value)} data-testid="fare-wait-minutes" />
          </Field>
          <Field label="Additional waiting ($, approved)" htmlFor="ff-xwait">
            <Input id="ff-xwait" type="number" min="0" step="0.01" value={f.extra_waiting} onChange={(e) => set("extra_waiting", e.target.value)} data-testid="fare-extra-waiting" />
          </Field>
          <Field label="Discount (one only)" htmlFor="ff-disc">
            <Select id="ff-disc" options={DISCOUNTS} value={f.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="fare-discount-select" />
          </Field>
          {f.discount_type === "courtesy" && (
            <Field label="Courtesy amount ($)" htmlFor="ff-disc-amt">
              <Input id="ff-disc-amt" type="number" min="0" step="0.01" value={f.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} data-testid="fare-discount-amount" />
            </Field>
          )}
        </div>
        <FeeList fees={f.fees} onChange={(v) => set("fees", v)} />
        <label className="flex min-h-[48px] items-center gap-3 text-[15px]">
          <input type="checkbox" className="h-6 w-6 accent-[#65238E]" checked={f.override} onChange={(e) => set("override", e.target.checked)} data-testid="fare-override-toggle" />
          Override the fare by hand
        </label>
        {f.override && (
          <Field label="Override fare ($, before stops, waiting, discount and fees)" htmlFor="ff-ovr">
            <Input id="ff-ovr" type="number" min="0" step="0.01" value={f.fare_override} onChange={(e) => set("fare_override", e.target.value)} data-testid="fare-override-input" />
          </Field>
        )}
      </div>
      <FareResult res={res} err={err} onIssue={onIssue ? issue : undefined} busy={busy} />
    </div>
  );
}

function FareResult({ res, err, onIssue, busy }: { res: any; err: string; onIssue?: () => void; busy: boolean }) {
  if (err) return <div><ErrorNote msg={err} /></div>;
  if (!res) return <p className="text-[15px] text-mist/65">Enter the trip miles to see the fare.</p>;
  const b = res.breakdown;
  const d = res.deposit;
  return (
    <div className="flex flex-col gap-4 rounded-md border border-gold/30 bg-ink p-5" data-testid="fare-result">
      {b.warnings.map((w: string) => (
        <p key={w} role="alert" className="flex gap-3 rounded-md border border-amber-300/50 bg-amber-400/10 p-3 text-[14px] text-amber-100" data-testid="fare-price-jump-warning">
          <AlertTriangle className="mt-0.5 shrink-0" size={18} /> {w}
        </p>
      ))}
      <dl className="flex flex-col divide-y divide-gold/15 text-[15px]">
        {b.line_items.map((i: any) => <Row key={i.label} k={i.label} v={usd(i.amount)} />)}
        <Row k="Subtotal" v={usd(b.subtotal)} />
        {b.discount > 0 && <Row k={b.discount_label} v={`-${usd(b.discount)}`} />}
        {b.fees.map((i: any) => <Row key={i.label} k={i.label} v={usd(i.amount)} />)}
      </dl>
      <div className="gold-rule" />
      <div className="flex items-baseline justify-between">
        <span className="font-display text-lg text-gold">Total</span>
        <span className="font-display text-3xl text-mist" data-testid="fare-total">{usd(b.total)}</span>
      </div>
      <p className="text-[14px] text-mist/60">Rules v{b.rule_version} · {usd(b.rate_per_mile)}/mi · base {usd(b.base_fare)}</p>
      {d && (
        <div className="rounded-md border border-gold/20 p-3 text-[14px]" data-testid="fare-deposit">
          <p className="text-gold">{d.rule}</p>
          <p className="mt-1">Due to confirm: {usd(d.deposit_amount)}{d.balance_due_at && <> · Balance {usd(d.balance_amount)} by {ct(d.balance_due_at)}</>}</p>
        </div>
      )}
      {onIssue && (
        <Btn onClick={onIssue} disabled={busy} data-testid="fare-issue-quote-btn"><Lock size={18} /> {busy ? "Issuing…" : "Issue locked quote"}</Btn>
      )}
    </div>
  );
}

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex justify-between gap-4 py-2"><dt className="text-mist/80">{k}</dt><dd className="tabular-nums">{v}</dd></div>
);

function AmountList({ label, items, onChange, testid, min, max }: { label: string; items: string[]; onChange: (v: string[]) => void; testid: string; min: number; max: number }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[14px] font-medium text-mist/90">{label}</span>
      {items.map((s, i) => (
        <div key={i} className="flex gap-2">
          <Input aria-label={`Stop ${i + 1} amount`} type="number" min={min} max={max} step="0.5" value={s} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${testid}-${i}`} />
          <Btn variant="quiet" aria-label="Remove" className="px-3" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><X size={18} /></Btn>
        </div>
      ))}
      <Btn variant="ghost" className="self-start" onClick={() => onChange([...items, String(min)])} data-testid={`${testid}-add-btn`}><Plus size={18} /> Add stop charge</Btn>
    </div>
  );
}

function FeeList({ fees, onChange }: { fees: { label: string; amount: string }[]; onChange: (v: any[]) => void }) {
  const upd = (i: number, k: string, v: string) => onChange(fees.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[14px] font-medium text-mist/90">Fees (added on top)</span>
      {fees.map((x, i) => (
        <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
          <Input aria-label="Fee label" placeholder="e.g. Tolls" value={x.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`fare-fee-label-${i}`} />
          <Input aria-label="Fee amount" type="number" min="0" step="0.01" value={x.amount} onChange={(e) => upd(i, "amount", e.target.value)} data-testid={`fare-fee-amount-${i}`} />
          <Btn variant="quiet" aria-label="Remove fee" className="px-3" onClick={() => onChange(fees.filter((_, j) => j !== i))}><X size={18} /></Btn>
        </div>
      ))}
      <Btn variant="ghost" className="self-start" onClick={() => onChange([...fees, { label: "", amount: "" }])} data-testid="fare-add-fee-btn"><Plus size={18} /> Add fee</Btn>
    </div>
  );
}
